import { HttpClient } from "@angular/common/http";
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { HasId } from './has-id.interface';

import { BasicDataSubService } from './basic-data-subservice.class';

export class ActivityAwareDataSubService<T extends HasId> extends BasicDataSubService<T> {
	constructor(
		httpClient: HttpClient,
		url: string,
		private activityChecker: (entity: T) => boolean,
		clearCacheCommand?: Observable<Date>
	) {
		super(
			httpClient,
			url,
			clearCacheCommand,
		);
	}
	
	public override clearCaches(): void {
		super.clearCaches();
		this.activeListCache = null;
		this.mapActiveByIdCache = null;
	}

	private activeListCache: Observable<T[]> | null = null;
	public getActiveList(): Observable<T[]> {
		if (!this.activeListCache) {
			this.activeListCache = this.getList().pipe(
				map(
					(response) => {
						let activeOnly = response.filter(
							this.activityChecker
						);

						return activeOnly;
					}
				),
				shareReplay(1)
			);
		}

		return this.activeListCache;
	}

	private mapActiveByIdCache: Observable<Map<number, T>> | null = null;
	public getMapActiveById(): Observable<Map<number, T>> {
		if (!this.mapActiveByIdCache) {
			this.mapActiveByIdCache = this.getActiveList().pipe(
				map(
					(list: T[]) => {
						let map = new Map<number, T>();
						for (let i = 0; i < list.length; i++) {
							const element = list[i];
							map.set(element.id, element);
						}
						return map;
					}
				),
				shareReplay(1)
			);
		}
		return this.mapActiveByIdCache;
	}
	
	public getActiveById(id: number): Observable<T | undefined> {
		return this.getMapActiveById().pipe(
			map(
				(map: Map<number, T>) => map.get(id)
			)
		);
	}

	public getKnownActiveById(id: number, typeName: string): Observable<T> {
		return this.getMapById().pipe(
			map(
				(map: Map<number, T>) => {
					const existant = map.get(id);
					if (!existant) {
						throw new Error("Unkown " + typeName + " #" + id);
					} else if (this.activityChecker(existant)) {
						return existant;
					} else {
						throw new Error(typeName + " #" + id + " is not active");
					}
				}
			)
		);
	}

	private mapActiveByForeignKeyCache = new Map<string, Observable<Map<number, T[]>>>();
	public getMapActiveByForeignKey(cacheKey: string, keyGetter: (entity: T) => number | null): Observable<Map<number, T[]>> {
		let cache = this.mapActiveByForeignKeyCache.get(cacheKey);
		if (!cache) {
			cache = this.getActiveList() //
				.pipe(
					map(
						(list: T[]) => {
							let map = new Map<number, T[]>();
							for (let i = 0; i < list.length; i++) {
								const element = list[i];
								const foreignKey = keyGetter(element);
								if (foreignKey != null) {
									let siblings = map.get(foreignKey);
									if (!siblings) {
										siblings = [];
										map.set(foreignKey, siblings);
									}
									siblings.push(element);
								}
							}
							return map;
						}
					)
				)
				;
			this.mapActiveByForeignKeyCache.set(cacheKey, cache);
		}
		return cache;
	}
	
	public getActiveByForeignKey(cacheKey: string, keyGetter: (entity: T) => number | null, key: number): Observable<T[]> {
		return this.getMapActiveByForeignKey(cacheKey, keyGetter).pipe(
			map(
				(map: Map<number, T[]>) => {
					const list = map.get(key);
					return list ? list : [];
				}
			)
		);
	}
}